import { useCallback, useEffect } from 'react';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse } from '@iwoca/lapi-client/introducer_portal';
import { Accordion, Pill, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { useFileUploadReducer } from 'components/Signup/hooks/useFileUploadReducer';
import { useLocale } from 'hooks/useLocale';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import {
  TMarkFeedbackAsDoneItem,
  useGetDocuments,
  useGetIntroducerApplication,
  useMarkFeedbackAsDone,
} from 'lendingApi/lendingApiHooks';
import _ from 'lodash';
import { getCookie } from 'utils/cookieHelper';
import { FileStatusGroup } from '../FileStatusGroup/FileStatusGroup';
import { MissingDocumentsGroup } from '../FileStatusGroup/MissingDocumentsGroup/MissingDocumentsGroup';
import { UploadDropButton } from '../UploadDropButton/UploadDropButton';
import styles from './DocumentGroup.module.css';
import {
  TDocumentTypeWithDE,
  FILE_STATUS_GROUPS,
  getAdditionalText,
  getInstruction,
  getRequirementsTitle,
  handleDocumentTypeMapping,
} from './DocumentGroupHelper';
import { LinkOpenBankingStatus } from './LinkOpenBankingStatus/LinkOpenBankingStatus';

export type TDocRequirementReview =
  GetApiIntroducerPortalIntroducerApplicationDocumentFeedbackResponse['data']['doc_requirement_reviews'][number];

type TProps = {
  requirement: TOfferDataRequirement['actions'][number];
  canUploadDocs: boolean;
  isLoading?: boolean;
  docRequirementReviews?: TDocRequirementReview[];
  hasDocumentFeedback?: boolean;
};

type TDocTypeStatus = {
  status: 'processing' | 'required' | 'completed';
  pillVariant: 'green' | 'london' | 'yellow' | 'red';
  labelText: string;
};

export const DocumentGroup = ({
  requirement,
  canUploadDocs,
  isLoading,
  docRequirementReviews,
  hasDocumentFeedback = false,
}: TProps) => {
  const { locale, isUK, isDE } = useLocale();
  const { application } = useGetIntroducerApplication();

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  // Below logic is mapping DE doc types to uk versions so we upload files to correct categories
  const documentType = handleDocumentTypeMapping(
    requirement.type as TDocumentTypeWithDE,
    locale,
  );

  const mappedRequirement = { ...requirement, type: documentType };

  const showDocumentFeedback =
    hasDocumentFeedback &&
    (isDE || mappedRequirement.type === 'bank_statement');

  const stateKey = application?.customer?.state_key;
  const { data: documents } = useGetDocuments(stateKey, showDocumentFeedback);

  const { mutate: markAsDone } = useMarkFeedbackAsDone();

  useEffect(() => {
    if (documents) {
      const documentsForThisDocumentType = documents?.data?.filter(
        (uploadedDocument) => {
          return documentType === uploadedDocument.type;
        },
      );

      setAllFiles(documentsForThisDocumentType, showDocumentFeedback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirement, documents, showDocumentFeedback]);

  const docReviewsForType = showDocumentFeedback
    ? docRequirementReviews?.filter(
        (review) =>
          review.resolution_method ===
          mappedRequirement.recommended_resolution_method,
      )
    : undefined;

  const getMissingCoverages = (markedAsDone: boolean) =>
    docReviewsForType
      ?.filter((review) => review.marked_as_done === markedAsDone)
      .flatMap((review) => review.bank_account_coverages)
      .filter((coverage) => coverage.missing_coverages.length > 0);

  const rejectedCoverages = getMissingCoverages(false);
  const overriddenCoverages = getMissingCoverages(true);

  const hasFeedback =
    uploadedFiles.some(
      (file) => file.docsFeedback?.length && file.statusGroup === 'rejected',
    ) || !!rejectedCoverages?.length;

  const handleFeedbackOnUpload = useCallback(() => {
    const feedbackIds = uploadedFiles
      .filter((file) => file.status === 'rejected')
      .flatMap((file) => file.docsFeedback ?? [])
      .filter((feedback) => !feedback.marked_as_done_by_customer)
      .map((feedback) => feedback.id);

    const requirementIds = (docReviewsForType ?? [])
      .filter((requirement) => !requirement.marked_as_done)
      .map((requirement) => requirement.id);

    const allItems: TMarkFeedbackAsDoneItem[] = [
      { type: 'file_feedback', ids: feedbackIds },
      { type: 'requirement', ids: requirementIds },
    ];
    const itemsWithIds = allItems.filter(({ ids }) => ids.length > 0);

    if (itemsWithIds.length) {
      markAsDone(itemsWithIds);
    }
  }, [uploadedFiles, docReviewsForType, markAsDone]);

  const getDocTypeStatus = (): TDocTypeStatus | undefined => {
    if (hasFeedback && mappedRequirement.status !== 'completed') {
      return {
        status: 'required',
        pillVariant: 'red',
        labelText: isUK ? t`Required` : t`Documents requested`,
      };
    }

    switch (mappedRequirement.status) {
      case 'pending':
        return {
          status: 'processing',
          pillVariant: 'london',
          labelText: t`Processing`,
        };
      case 'error':
      case 'required':
        return {
          status: 'required',
          pillVariant: 'red',
          labelText: t`Required`,
        };
      case 'completed':
        return {
          status: 'completed',
          pillVariant: 'green',
          labelText: t`Completed`,
        };
    }
  };

  const documentTypeStatus = getDocTypeStatus();
  const isBankStatement = mappedRequirement.type === 'bank_statement';
  const showOpenBanking = isBankStatement && isUK;

  // The generic "check your email" box for non-bank-statement docs is still
  // being validated, so it sits behind a cookie for now. Bank-statement
  // coverage feedback ships regardless.
  const showGenericMissingDocs = getCookie('showGenericMissingDocs') === 'true';

  const hasMissingContent = isBankStatement
    ? !!rejectedCoverages?.length
    : showGenericMissingDocs && uploadedFiles.length > 0;

  const showMissingBlock =
    isUK &&
    hasDocumentFeedback &&
    !mappedRequirement.is_optional &&
    documentTypeStatus?.status === 'required' &&
    hasMissingContent;

  const additionalText = isDE && getAdditionalText(mappedRequirement.type);

  const renderPill = (docType?: TDocTypeStatus) => {
    if (!docType) {
      return null;
    }

    return (
      <Pill
        className={clsx(styles.pill, styles[docType.status])}
        variant={docType.pillVariant}
      >
        {isLoading ? (
          <Spinner className={styles.spinner} size="small" />
        ) : (
          _.upperFirst(docType.labelText)
        )}
      </Pill>
    );
  };

  const getDocumentRequirementUploadType = (docType?: string) => {
    if (!docType) {
      return null;
    }

    return docType
      .split('_')
      .join(' ') as PostDocumentUploadResponse['data']['document_type'];
  };

  if (!stateKey) {
    return null;
  }

  return (
    <Accordion
      className={styles.accordion}
      titleClassName={styles.titleContainer}
      title={
        <>
          <p className={styles.title}>
            {getRequirementsTitle(mappedRequirement.type)}
            <span className={styles.optional}>
              {mappedRequirement.is_optional && ' (Optional)'}
            </span>
          </p>
          {!mappedRequirement.is_optional && renderPill(documentTypeStatus)}
        </>
      }
    >
      <>
        {documentTypeStatus?.status === 'required' && (
          <>
            <p className={styles.instruction}>
              {getInstruction(
                mappedRequirement.type,
                mappedRequirement.quantifier?.value,
                mappedRequirement.quantifier?.unit,
              )}
            </p>
            {additionalText && (
              <p className={styles.additionalText}>{additionalText}</p>
            )}
          </>
        )}
        {documentTypeStatus?.status === 'processing' && isDE && (
          <p className={styles.instruction}>
            <Trans>
              The document upload was successful. The review is underway.
            </Trans>
          </p>
        )}
        <UploadDropButton
          setFileLoading={setFileLoading}
          setFileSuccess={setFileSuccess}
          setFileError={setFileError}
          setFileReject={setFileReject}
          stateKey={stateKey}
          isOptional={mappedRequirement.is_optional}
          documentType={getDocumentRequirementUploadType(documentType)}
          isDisabled={!canUploadDocs}
          onSuccess={hasFeedback ? handleFeedbackOnUpload : undefined}
        />
        {showMissingBlock && (
          <MissingDocumentsGroup
            coverages={isBankStatement ? rejectedCoverages : undefined}
          />
        )}
        {FILE_STATUS_GROUPS.map((statusGroup) => {
          // UK renders these coverages in the standalone Missing box above, not inline.
          const missingCoverages = isUK
            ? undefined
            : statusGroup === 'rejected'
              ? rejectedCoverages
              : statusGroup === 'overridden'
                ? overriddenCoverages
                : undefined;

          return (
            <FileStatusGroup
              key={`${mappedRequirement.type}-${statusGroup}`}
              testId={statusGroup}
              statusGroup={statusGroup}
              files={uploadedFiles}
              removeFile={removeFile}
              missingCoverages={missingCoverages}
            />
          );
        })}
        {showOpenBanking && <LinkOpenBankingStatus />}
      </>
    </Accordion>
  );
};
